/*
 * The managed writes: every change the About column, the header band, the rename dialog, the owner
 * section, the conversation's ⋯ menu (its switch for AI chats) and the administrator's outside view
 * make to a conversation. They are one home because
 * two rules hold for all of them, and a component that called `api.ts` directly would have to
 * remember both:
 *
 *  - An administrator changes a channel or private channel they are not in through the matrix's
 *    admin branch, whose cells ask for a fresh password (`AU without M`). Every write therefore runs
 *    inside `withStepUp`, which asks for the password when the server says so and is a plain call for
 *    a member, whose cells ask for nothing.
 *  - Nothing the server publishes reaches a reader without a row: hub delivery is by membership. A
 *    conversation the reader holds without an active row — an administrator's outside view
 *    (`ChatSlice.outside`), or a channel an administrator only browses through a group of theirs —
 *    would keep showing what it was before the write, so once a write committed it is read again
 *    (`refreshOutside`), also when a later write of the same change was refused. A conversation the
 *    reader is in learns of its own change from the stream, and is never read again here.
 *
 * The routes themselves stay spelled in `api.ts` alone; this file only wraps them.
 */

import { withStepUp } from "../../../core/auth/index.ts"
import type { ConversationId, GroupId, UserId } from "../../../core/store/model.ts"
import { client } from "../../../core/store/store.ts"
import { refreshOutside } from "../state/history.ts"
import { chatSlice } from "../state/selectors.ts"
import type { UpdateConversation } from "../wire/chat.gen.ts"
import type { ConversationResponse, HistoryMode, MembersAdded } from "./api.ts"
import * as routes from "./api.ts"

/**
 * Whether nothing the server publishes about `conv` reaches this reader although the model draws it:
 * an outside view, or a conversation in the model without the reader's active row.
 */
function heldWithoutRow(conv: ConversationId): boolean {
	const model = client.api.getState()
	if (chatSlice(model).outside[conv] !== undefined) return true
	return model.conversations[conv] !== undefined && chatSlice(model).self[conv]?.state !== "active"
}

/**
 * Runs one change — one or more writes, each sent through `send`, which puts it behind the step-up —
 * and, once any of them committed, re-reads a conversation the reader holds without a row. The read
 * is taken after the last write, in `finally`, so it sees what committed even when a later write of
 * the change was refused.
 */
async function managed<T>(
	conv: ConversationId,
	change: (send: <R>(write: () => Promise<R>) => Promise<R>) => Promise<T>,
): Promise<T> {
	let committed = false
	const send = async <R>(write: () => Promise<R>): Promise<R> => {
		const answer = await withStepUp(write)
		committed = true
		return answer
	}
	try {
		return await change(send)
	} finally {
		if (committed && heldWithoutRow(conv)) await refreshOutside(conv)
	}
}

/** Rewrites names, topic or purpose; an absent field is left as it is. */
export function updateConversation(
	conv: ConversationId,
	patch: UpdateConversation,
): Promise<ConversationResponse> {
	return managed(conv, (send) => send(() => routes.updateConversation(conv, patch)))
}

/** Adds people, with the history they may read; an administrator adding themself joins in the open. */
export function addMembers(
	conv: ConversationId,
	userIds: UserId[],
	history: HistoryMode,
): Promise<MembersAdded> {
	return managed(conv, (send) => send(() => routes.addMembers(conv, userIds, history)))
}

/** Takes one person out of a conversation (never oneself: that is leaving). */
export function removeMember(conv: ConversationId, userId: UserId): Promise<void> {
	return managed(conv, (send) => send(() => routes.removeMember(conv, userId)))
}

/**
 * Opens the conversation to AI chats, or closes it. A member switches it as themself; an
 * administrator outside a channel or a private channel through the step-up, like every other write
 * from outside. The line everybody in the conversation reads is the server's to write.
 */
export function setAgents(conv: ConversationId, available: boolean): Promise<ConversationResponse> {
	return managed(conv, (send) => send(() => routes.setAgents(conv, available)))
}

/**
 * Moves a channel's owner groups from `current` to `next`. The new groups are added first, in one
 * call, and the dropped ones removed after, one by one: at no point does the channel hold fewer
 * groups than it will at the end, so the server's last-owner rule is never what stops a change the
 * person asked for. An empty `next` is never applied (a channel keeps at least one group), and a
 * `next` equal to `current` sends nothing. Answers the server's last view of the channel, or
 * `undefined` when nothing was sent.
 */
export function changeOwners(
	conv: ConversationId,
	current: readonly GroupId[],
	next: readonly GroupId[],
): Promise<ConversationResponse | undefined> {
	const adds = next.filter((id) => !current.includes(id))
	const removes = current.filter((id) => !next.includes(id))
	if (next.length === 0 || (adds.length === 0 && removes.length === 0)) {
		return Promise.resolve(undefined)
	}
	return managed(conv, async (send) => {
		let last: ConversationResponse | undefined
		if (adds.length > 0) last = await send(() => routes.addOwners(conv, adds))
		for (const id of removes) last = await send(() => routes.removeOwner(conv, id))
		return last
	})
}
